<template>
  <v-navigation-drawer
    v-model="state.open"
    right
    temporary
    app
    class="proposed-pay-plan-drawer-connected"
    :width="drawerWidth"
  >
    <div v-if="state.loading" class="pppdc__loading" data-testid="pppd-loading">
      <v-progress-circular indeterminate color="primary" size="32" />
    </div>

    <!-- ECL design component (ER-12110), fed by the composable mapping -->
    <ProposedPayPlanDrawer
      v-else-if="drawerProps"
      :plan-type="drawerProps.planType"
      :approval-status="drawerProps.approvalStatus"
      :fte-adjusted="drawerProps.fteAdjusted"
      :readiness-enabled="drawerProps.readinessEnabled"
      :range-penetration="drawerProps.rangePenetration"
      :proposal="drawerProps.proposal"
      :payRange1Fte="drawerProps.payRange1Fte"
      :pay-range-adjusted="drawerProps.payRangeAdjusted"
      :approvers="drawerProps.approvers"
      :current-plan="drawerProps.currentPlan"
      :error="state.error || ''"
      @submit="submit"
      @execute="execute"
      @commit="openReconcile"
      @reset-to-draft="openReset"
      @delete="openDeleteConfirm"
      @void="openVoid"
      @add-approver="openApproverPicker"
      @remove-approver="onRemoveApprover"
      @update:proposal="onProposalUpdate"
      @open-chatter="
        $emit('open-chatter', {
          id: state.proposal?.id,
          versionCode: state.proposal?.version_code,
        })
      "
      @close="close"
      @propose-new-plan="$emit('propose-new-plan')"
    />

    <!-- Approver picker (the ECL only emits add-approver) -->
    <div v-if="state.approverPickerOpen" class="pppdc__picker-overlay">
      <div class="pppdc__picker-card">
        <ExSelectPicker
          :key="approverPickerKey"
          pickerLabel="Approver"
          label="Pick an employee"
          searchLabel="Search an employee"
          placeholder="Search an employee"
          :items="availableApproverItems"
          :value="null"
          :isEmployeePicker="true"
          :hasImageItem="false"
          :isSelectedItemRemove="false"
          @item-selected="onApproverSelected"
          @input="onApproverSelected"
        />
      </div>
    </div>

    <!-- Commit / Reconcile dialog (Rule B-12: the checkbox gates Commit) -->
    <PayPlanDialog
      :open="state.reconcileOpen"
      title="Commit pay plan"
      :submitting="state.submitting"
      @close="state.reconcileOpen = false"
    >
      <template #icon>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path
            d="M16.667 9.233V10a6.667 6.667 0 1 1-3.953-6.094"
            stroke="#7f56d9"
            stroke-width="1.67"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M17.5 3.333 10 10.842l-2.25-2.25"
            stroke="#7f56d9"
            stroke-width="1.67"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </template>

      <template #description>
        Verify these terms against payroll before committing.
      </template>

      <CheckboxInput
        v-model="state.payrollVerified"
        label="I've verified these terms against payroll"
        data-testid="pppd-payroll-verified"
      />
      <CheckboxInput
        v-model="state.notifyEmployee"
        label="Notify the employee"
        data-testid="pppd-notify-employee"
      />

      <template #footer>
        <StandardButton
          type="magenta"
          variant="outline"
          size="normal"
          label="Cancel"
          :disabled="state.submitting"
          @click="state.reconcileOpen = false"
        />
        <StandardButton
          type="magenta"
          variant="primary"
          size="normal"
          label="Commit"
          data-testid="pppd-commit-confirm"
          :disabled="!state.payrollVerified || state.submitting"
          :loader-left="state.submitting"
          @click="reconcile"
        />
      </template>
    </PayPlanDialog>

    <!-- Void dialog (Rule B-14: reason gates confirm) -->
    <PayPlanDialog
      :open="state.voidOpen"
      title="Void proposal"
      :submitting="state.submitting"
      @close="state.voidOpen = false"
    >
      <template #icon>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path
            d="M10 6.667v3.333m0 3.333h.008M8.575 2.283 1.517 14.167a1.667 1.667 0 0 0 1.425 2.5h14.116a1.667 1.667 0 0 0 1.425-2.5L11.425 2.283a1.667 1.667 0 0 0-2.85 0Z"
            stroke="#d92d20"
            stroke-width="1.67"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </template>

      <template #description>
        This permanently voids the proposal and frees the employee for a new one.
      </template>

      <label class="pppdc__field-label" for="pppd-void-reason">Reason</label>
      <v-textarea
        id="pppd-void-reason"
        v-model="state.voidReason"
        data-testid="pppd-void-reason"
        rows="3"
        auto-grow
        hide-details
        outlined
        dense
      />

      <template #footer>
        <StandardButton
          type="magenta"
          variant="outline"
          size="normal"
          label="Cancel"
          :disabled="state.submitting"
          @click="state.voidOpen = false"
        />
        <StandardButton
          type="red"
          variant="primary"
          size="normal"
          label="Void"
          data-testid="pppd-void-confirm"
          :disabled="!state.voidReason.trim() || state.submitting"
          :loader-left="state.submitting"
          @click="voidProposal"
        />
      </template>
    </PayPlanDialog>

    <!-- Reset dialog (Rule B-6/B-16: optional notify-approvers) -->
    <PayPlanDialog
      :open="state.resetOpen"
      title="Reset to draft"
      :submitting="state.submitting"
      @close="state.resetOpen = false"
    >
      <template #icon>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path
            d="M3.333 6.667h9.167a4.167 4.167 0 1 1 0 8.333H8.333M3.333 6.667l3.334-3.334M3.333 6.667l3.334 3.333"
            stroke="#7f56d9"
            stroke-width="1.67"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </template>

      <template #description>
        Cancel the current approval round and return the proposal to draft.
      </template>

      <CheckboxInput
        v-model="state.notifyApprovers"
        label="Notify approvers"
        data-testid="pppd-notify-approvers"
      />

      <template #footer>
        <StandardButton
          type="magenta"
          variant="outline"
          size="normal"
          label="Cancel"
          :disabled="state.submitting"
          @click="state.resetOpen = false"
        />
        <StandardButton
          type="magenta"
          variant="primary"
          size="normal"
          label="Reset"
          data-testid="pppd-reset-confirm"
          :disabled="state.submitting"
          :loader-left="state.submitting"
          @click="resetToDraft"
        />
      </template>
    </PayPlanDialog>

    <!-- Delete confirmation (Rule B-19: draft only) -->
    <PayPlanDialog
      :open="state.deleteConfirmOpen"
      title="Delete proposal"
      :submitting="state.submitting"
      @close="state.deleteConfirmOpen = false"
    >
      <template #icon>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path
            d="M10 6.667v3.333m0 3.333h.008M8.575 2.283 1.517 14.167a1.667 1.667 0 0 0 1.425 2.5h14.116a1.667 1.667 0 0 0 1.425-2.5L11.425 2.283a1.667 1.667 0 0 0-2.85 0Z"
            stroke="#d92d20"
            stroke-width="1.67"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </template>

      <template #description>
        This permanently removes the draft proposal. This cannot be undone.
      </template>

      <template #footer>
        <StandardButton
          type="magenta"
          variant="outline"
          size="normal"
          label="Cancel"
          :disabled="state.submitting"
          @click="state.deleteConfirmOpen = false"
        />
        <StandardButton
          type="red"
          variant="primary"
          size="normal"
          label="Delete"
          data-testid="pppd-delete-confirm"
          :disabled="state.submitting"
          :loader-left="state.submitting"
          @click="deleteProposal"
        />
      </template>
    </PayPlanDialog>
  </v-navigation-drawer>
</template>

<script setup>
import { computed, ref } from "vue";
import CheckboxInput from "@components/Checkbox/CheckboxInput.vue";
import PayPlanDialog from "@components/PayPlanDialog/PayPlanDialog.vue";
import ExSelectPicker from "@components/SelectPicker/ExSelectPicker.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import ProposedPayPlanDrawer from "./ProposedPayPlanDrawer.vue";
import { useProposalDrawer } from "./composables/useProposalDrawer";

const props = defineProps({
  proxy: {
    type: Object,
    default: null,
  },
  fetchRows: {
    type: Function,
    default: null,
  },
});

const emit = defineEmits(["open-chatter", "propose-new-plan"]);

const {
  state,
  drawerProps,
  open,
  close,
  submit,
  execute,
  resetToDraft,
  reconcile,
  deleteProposal,
  voidProposal,
  updateEffectiveFrom,
  addApprover,
  removeApprover,
} = useProposalDrawer({ proxy: props.proxy, fetchRows: props.fetchRows });

function openWithVoidDialog(args) {
  open(args);
  openVoid();
}

defineExpose({ open, close, openWithVoidDialog });

/** Wide panel — the ECL layout has 2–3 column field rows (matches the story's canvas). */
const drawerWidth = ref(720);

function openReconcile() {
  state.payrollVerified = false;
  state.notifyEmployee = true;
  state.reconcileOpen = true;
}

function openVoid() {
  state.voidReason = "";
  state.voidOpen = true;
}

function openReset() {
  state.notifyApprovers = false;
  state.resetOpen = true;
}

function openDeleteConfirm() {
  state.deleteConfirmOpen = true;
}

const approverPickerKey = ref(0);

const selectedApproverIds = computed(
  () => new Set((state.proposal?.approvers || []).map((approver) => approver.id))
);

const availableApproverItems = computed(() =>
  (state.approverItems || []).filter((item) => !selectedApproverIds.value.has(item.id))
);

function openApproverPicker() {
  state.approverPickerOpen = true;
}

function closeApproverPicker() {
  state.approverPickerOpen = false;
  approverPickerKey.value += 1;
}

async function onApproverSelected(item) {
  if (!item?.id) return;
  if (selectedApproverIds.value.has(item.id)) {
    closeApproverPicker();
    return;
  }
  await addApprover(item);
  closeApproverPicker();
}

function onRemoveApprover(index) {
  const approver = (state.proposal?.approvers || [])[index];
  if (!approver) return;
  // Detail rows key on the Access id; freshly added rows carry their employee id.
  removeApprover({
    id: approver.id,
    accessId: approver.accessId ?? approver.id,
    employeeId: approver.employeeId ?? null,
  });
}

/** The ECL emits the whole updated proposal; persist the effective date change. */
function onProposalUpdate(payload) {
  state.proposal = { ...(state.proposal || {}), ...payload };
  if (payload?.effective_from) {
    updateEffectiveFrom(payload.effective_from);
  }
}
</script>

<style scoped>
.pppdc__loading {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}

.pppdc__picker-overlay {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 80px 16px;
  background: rgba(16, 24, 40, 0.45);
}

.pppdc__picker-card {
  width: 420px;
  max-width: 100%;
  padding: 20px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 8px 16px -4px rgba(16, 24, 40, 0.1);
}

.pppdc__field-label {
  margin-bottom: 6px;
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}
</style>
